/**
 * Cognito authentication wrapper.
 *
 * Encapsulates amazon-cognito-identity-js so the rest of the
 * app uses simple async functions. Handles SRP authentication,
 * NEW_PASSWORD_REQUIRED challenge for first-time logins, the
 * ForgotPassword / ConfirmForgotPassword flow for a signed-out
 * user resetting their password, token storage (LocalStorage via
 * the library's default), and session refresh.
 *
 * Tokens persist across browser sessions via LocalStorage.
 * Refresh tokens are valid 30 days (per Cognito client config).
 */
import { friendlyPasswordPolicyMessage } from "./passwordPolicy";
import {
  CognitoUserPool, CognitoUser, AuthenticationDetails,
  type CognitoUserSession, type CognitoUserAttribute,
} from "amazon-cognito-identity-js";
// Role predicates/constants live in a side-effect-free sibling so pages can
// import them without dragging this module's eager CognitoUserPool
// construction into their bundle/test graph — see authRole.ts's header.
import { ORG_ADMIN_ROLE } from "./authRole";

const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID!;
const ClientId   = import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID!;

if (!UserPoolId || !ClientId) {
  throw new Error(
    "Missing Cognito config. Set VITE_COGNITO_USER_POOL_ID and " +
    "VITE_COGNITO_USER_POOL_CLIENT_ID in .env.local.",
  );
}

const userPool = new CognitoUserPool({
  UserPoolId,
  ClientId,
});

/** Fields every signed-in identity carries, whatever its scoping level. */
export interface AuthenticatedUserBase {
  username: string;
  email: string;
  idToken: string;
  accessToken: string;
}

/**
 * A TENANT-scoped user — the only shape this module produced before
 * round7/org-admin-panel-ui.
 *
 * round7/d3-scope-config-portal: resolves DEBT.md's round4/builder-
 * templates-portal entry ("once a role claim is available on
 * AuthenticatedUser..."). DockPro-Backend's AuthStack.ts (round0/
 * multi-user-model) added `custom:role` (owner|rep) to the Cognito pool
 * and its app client's readAttributes, and portalAuthorizer.ts already
 * reads it server-side — this just decodes the same claim client-side,
 * mirroring how `tenantId` is already read from `custom:tenantId`.
 * Existing owner-only actions (createTemplate/deleteTemplate/
 * createCheckoutSession — see their doc comments in lib/api.ts) predate
 * this and are NOT retrofitted to gate on it here; only SettingsPage's
 * designer-preset section (round7/d3-scope-config-portal) consumes it so
 * far. See roleFromClaim() below for the exact grandfathering default.
 */
export interface TenantUser extends AuthenticatedUserBase {
  role: "owner" | "rep";
  tenantId: string;
}

/**
 * An ORG-scoped user (P4.6-C) — a white-label holder's or enterprise org's
 * administrator. Carries `orgId` and DELIBERATELY NO `tenantId`.
 */
export interface OrgAdminUser extends AuthenticatedUserBase {
  role: "orgAdmin";
  orgId: string;
}

/**
 * ============================================================================
 * WHY THIS IS A DISCRIMINATED UNION AND NOT A WIDENED FLAT OBJECT (P4.6-C)
 * ============================================================================
 * The obvious "small" extension would have been to widen `role` and bolt an
 * optional `orgId?: string` onto the one existing shape. That was rejected:
 * it produces an object that is a lie for BOTH roles. An org admin would
 * carry `tenantId: ""` (there is no tenant — `sessionToUserWithAttrs`'s
 * existing `|| ""` fallback would have manufactured one), and an owner/rep
 * would carry `orgId: undefined`. Every consumer would then have to know,
 * by convention rather than by type, which half of the object is real.
 *
 * The union instead makes the two shapes exactly as different as the TOKENS
 * are. This mirrors DockPro-Backend's own contract rather than inventing a
 * client-side model of it — portalAuthorizer.ts branches on
 * `role === "orgAdmin"` and requires `custom:orgId` INSTEAD of
 * `custom:tenantId` (injecting `{orgId, role}` rather than
 * `{tenantId, role}`), and portalRequest.ts's `getPortalContext` now
 * explicitly THROWS if an orgAdmin context reaches a tenant-scoped handler.
 * An org-admin ID token genuinely has no `custom:tenantId` claim at all, so
 * a type that can produce one is a type that can produce a request the
 * backend is guaranteed to refuse.
 *
 * THE COST, STATED: exactly one existing line had to narrow —
 * LeadDetailPage.tsx's `if (!user) return null` became
 * `if (!user || !isTenantUser(user)) return null`. That is the union doing
 * its job: that page reads `user.tenantId` to build an embed URL, and it is
 * genuinely unreachable for an org admin (ProtectedRoute redirects them
 * away). No other call site anywhere reads `tenantId` off the auth user,
 * and `user.role === "owner"` comparisons (BillingPage, SettingsPage,
 * LeadDetailPage) narrow for free against a literal union. Every existing
 * owner/rep test passes unmodified.
 */
export type AuthenticatedUser = TenantUser | OrgAdminUser;

/**
 * Mirrors portalAuthorizer.ts's own grandfathering default exactly
 * (`(payload["custom:role"] as string | undefined) || "owner"`): a token/
 * attribute with no `custom:role` claim at all (every user provisioned
 * before round0/multi-user-model) is treated as "owner".
 *
 * P4.6-C widens the recognized set by exactly ONE literal, "orgAdmin", and
 * changes nothing else. Note what is deliberately preserved: "owner" remains
 * the CATCH-ALL for every unrecognized value, so a token carrying some future
 * role this build has never heard of still grandfathers to owner rather than
 * being locked out — the same posture the authorizer takes. Only the three
 * literals below are ever treated as anything but owner.
 */
function roleFromClaim(raw: unknown): "owner" | "rep" | "orgAdmin" {
  if (raw === "rep") return "rep";
  if (raw === ORG_ADMIN_ROLE) return "orgAdmin";
  return "owner";
}

/**
 * Assemble an `AuthenticatedUser` from already-extracted claim values, or
 * `null` when the claims cannot form a coherent identity of EITHER shape.
 *
 * Returning null for an orgAdmin with no `orgId` is the client-side mirror of
 * portalAuthorizer.ts throwing `Unauthorized` for exactly that token: such an
 * account is mis-provisioned, every `/api/portal/*` call it makes will be
 * refused, and signing it in would produce a session that can only render
 * errors. Better to refuse it here, with a message that names the cause.
 *
 * ASYMMETRY, DELIBERATE: the owner/rep branch keeps its pre-existing
 * permissive behavior (an absent `custom:tenantId` still yields `""` via the
 * callers below) rather than being tightened to the same standard. That
 * looseness is pre-existing, is equally refused by the authorizer, and
 * tightening it would change live behavior for tenant users — which this
 * round's own bar ("provably additive; existing owner/rep tests keep passing
 * unchanged") puts out of scope. The new branch has no legacy to preserve,
 * so it is strict from the start.
 */
function buildUser(
  base: AuthenticatedUserBase,
  role: "owner" | "rep" | "orgAdmin",
  tenantId: string,
  orgId: string,
): AuthenticatedUser | null {
  if (role === "orgAdmin") {
    if (!orgId) return null;
    return { ...base, role, orgId };
  }
  return { ...base, role, tenantId };
}

/** Shown when a Cognito session is valid but its claims are incoherent — see
 * buildUser. Exported so tests assert the exact citation. */
export const MISCONFIGURED_ACCOUNT_MESSAGE =
  "Your account is not fully set up: it is marked as an organization " +
  "administrator but has no organization assigned. Contact BlueCrane support.";

export type AuthChallenge =
  | { kind: "success"; user: AuthenticatedUser }
  | { kind: "new_password_required"; cognitoUser: CognitoUser }
  // `code` (round11/password-safeguards): Cognito's error code, additive and
  // optional, so LoginPage can offer the right next step for
  // PasswordResetRequiredException without parsing the message.
  | { kind: "error"; message: string; code?: string };

/**
 * Initiate sign-in with username + password.
 * Returns either a success (user authenticated), a challenge
 * (NEW_PASSWORD_REQUIRED for first-time login), or an error.
 */
export function signIn(username: string, password: string): Promise<AuthChallenge> {
  return new Promise((resolve) => {
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    const authDetails = new AuthenticationDetails({
      Username: username, Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => resolve(challengeFromSession(cognitoUser, session)),
      onFailure: (err) => resolve({
        kind: "error",
        message: friendlyAuthError(err),
        code: (err as Error & { code?: string }).code,
      }),
      // Cognito passes (userAttributes, requiredAttributes) here; we don't use
      // either — the challenge is handled by completeNewPassword later — so omit
      // them rather than carry unused params.
      newPasswordRequired: () => {
        resolve({ kind: "new_password_required", cognitoUser });
      },
    });
  });
}

/**
 * Complete a NEW_PASSWORD_REQUIRED challenge with a new
 * permanent password. Builders who were provisioned via
 * provisionTenant.ts hit this on first login.
 */
export function completeNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string,
): Promise<AuthChallenge> {
  return new Promise((resolve) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => resolve(challengeFromSession(cognitoUser, session)),
      onFailure: (err) => resolve({
        kind: "error",
        message: friendlyAuthError(err),
      }),
    });
  });
}

export type ForgotPasswordResult =
  | { kind: "sent" }
  | { kind: "error"; message: string };

/**
 * Request a password-reset code (Cognito `ForgotPassword`).
 *
 * By design this must never reveal whether `username` has an
 * account: the user pool client has `preventUserExistenceErrors`
 * enabled (see DockPro-Backend's `AuthStack.ts`), so Cognito itself
 * resolves this as a normal "sent" outcome even for an unknown
 * email, rather than throwing `UserNotFoundException`. The
 * `UserNotFoundException` branch below is a defensive backstop
 * in case that pool setting is ever missing/changed — it collapses
 * to the same "sent" result rather than surfacing a distinct
 * "no such user" message, so there is no alternate code path that
 * could leak account existence.
 */
export function forgotPassword(username: string): Promise<ForgotPasswordResult> {
  return new Promise((resolve) => {
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    cognitoUser.forgotPassword({
      onSuccess: () => resolve({ kind: "sent" }),
      onFailure: (err: Error & { code?: string }) => {
        if (err.code === "UserNotFoundException") {
          resolve({ kind: "sent" });
          return;
        }
        resolve({ kind: "error", message: friendlyForgotPasswordError(err) });
      },
    });
  });
}

export type ConfirmForgotPasswordResult =
  | { kind: "success" }
  | { kind: "error"; message: string };

/**
 * Complete a password reset with the code from `forgotPassword`
 * (Cognito `ConfirmForgotPassword`). Does not sign the user in —
 * matching Cognito's own behavior, they still sign in normally
 * afterward with the new password.
 *
 * Same no-leak rule as `forgotPassword`: `UserNotFoundException`
 * collapses into the same "bad code" message a real account would
 * see for a wrong/expired code, rather than a distinct message.
 */
export function confirmForgotPassword(
  username: string,
  code: string,
  newPassword: string,
): Promise<ConfirmForgotPasswordResult> {
  return new Promise((resolve) => {
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve({ kind: "success" }),
      onFailure: (err: Error & { code?: string }) => {
        // Same defensive collapse as forgotPassword() above: with
        // preventUserExistenceErrors enabled, Cognito itself should mask
        // this as CodeMismatchException already, but don't rely solely on
        // that — never let this specific code reach the UI as a distinct
        // message. (Not routed through friendlyConfirmForgotPasswordError's
        // default branch, which would echo err.message — a real
        // UserNotFoundException's message could differ in wording from a
        // real wrong-code message, which is itself a distinguishing signal.)
        if (err.code === "UserNotFoundException") {
          resolve({ kind: "error", message: "That code is incorrect. Please check and try again." });
          return;
        }
        resolve({ kind: "error", message: friendlyConfirmForgotPasswordError(err) });
      },
    });
  });
}

/**
 * Return the current user from session storage, or null if
 * none. Verifies the session is still valid (refreshing
 * access token via refresh token if needed).
 */
export function getCurrentUser(): Promise<AuthenticatedUser | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession) => {
      if (err || !session.isValid()) {
        resolve(null);
        return;
      }
      // Need attributes for tenant ID; load them.
      cognitoUser.getUserAttributes((attrErr, attrs) => {
        if (attrErr || !attrs) {
          resolve(null);
          return;
        }
        resolve(sessionToUserWithAttrs(cognitoUser, session, attrs));
      });
    });
  });
}

/**
 * Sign out the current user. Clears tokens from LocalStorage.
 */
export function signOut(): void {
  const cognitoUser = userPool.getCurrentUser();
  cognitoUser?.signOut();
}

// === Helpers ===

/**
 * Turn a freshly-authenticated session into the `AuthChallenge` both sign-in
 * entry points resolve with. Shared by `signIn` and `completeNewPassword` so
 * the incoherent-claims outcome cannot be handled in one and forgotten in the
 * other.
 *
 * A null from `sessionToUser` means Cognito authenticated the credentials but
 * the resulting identity is unusable (see buildUser). That surfaces as a
 * normal `kind: "error"` — the same channel a wrong password uses — because
 * from the person's point of view it is exactly that: they cannot get in, and
 * the message tells them who to ask. It is deliberately NOT a thrown
 * exception: nothing went wrong in this client.
 */
function challengeFromSession(
  cognitoUser: CognitoUser,
  session: CognitoUserSession,
): AuthChallenge {
  const user = sessionToUser(cognitoUser, session);
  if (!user) return { kind: "error", message: MISCONFIGURED_ACCOUNT_MESSAGE };
  return { kind: "success", user };
}

/**
 * Build from the ID TOKEN PAYLOAD — the path taken right after a successful
 * `authenticateUser`, when no `getUserAttributes()` round trip has happened
 * yet. P4.6-C adds `custom:orgId` alongside the `custom:tenantId` read that
 * was already here, exactly as portalAuthorizer.ts reads both off the same
 * verified payload.
 */
function sessionToUser(
  cognitoUser: CognitoUser,
  session: CognitoUserSession,
): AuthenticatedUser | null {
  const payload = session.getIdToken().payload;
  return buildUser(
    {
      username: cognitoUser.getUsername(),
      email: payload.email as string,
      idToken: session.getIdToken().getJwtToken(),
      accessToken: session.getAccessToken().getJwtToken(),
    },
    roleFromClaim(payload["custom:role"]),
    payload["custom:tenantId"] as string,
    payload["custom:orgId"] as string,
  );
}

/**
 * Build from `getUserAttributes()` — the path taken at app boot by
 * `getCurrentUser`. Note this is the one that matters for a page reload: an
 * attribute read, not a claim read, so `custom:orgId` must be readable by
 * this app client (AuthStack.ts's ReadAttributes) for an org admin to survive
 * a refresh. Verified against DEV — see the round report.
 */
function sessionToUserWithAttrs(
  cognitoUser: CognitoUser,
  session: CognitoUserSession,
  attrs: CognitoUserAttribute[],
): AuthenticatedUser | null {
  const attrMap: Record<string, string> = {};
  for (const a of attrs) attrMap[a.getName()] = a.getValue();
  return buildUser(
    {
      username: cognitoUser.getUsername(),
      email: attrMap.email || "",
      idToken: session.getIdToken().getJwtToken(),
      accessToken: session.getAccessToken().getJwtToken(),
    },
    roleFromClaim(attrMap["custom:role"]),
    attrMap["custom:tenantId"] || "",
    attrMap["custom:orgId"] || "",
  );
}

function friendlyAuthError(err: Error & { code?: string }): string {
  // Map Cognito error codes to user-friendly messages.
  // preventUserExistenceErrors is enabled on the client, so
  // unknown-user and wrong-password collapse to one message.
  switch (err.code) {
    case "NotAuthorizedException":
      return "Invalid email or password.";
    case "PasswordResetRequiredException":
      // round11/password-safeguards: this is what a builder sees after WE
      // start a reset on their behalf (admin-reset-user-password) — the old
      // password is dead and a code is already in their inbox. Say so, and
      // LoginPage adds the one-button path to the code screen.
      return "A password reset was started for this account, so your previous " +
        "password no longer works. Use the code we emailed you to set a new one.";
    case "UserNotConfirmedException":
      return "Account not confirmed. Please contact support.";
    case "InvalidPasswordException":
      return friendlyPasswordPolicyMessage(err.message);
    case "LimitExceededException":
      return "Too many attempts. Please wait a few minutes.";
    case "InvalidParameterException":
      return err.message || "Invalid input.";
    // round7/signup-ui (P4.5-B) — DockPro-Backend's preAuthEmailVerifiedGate.ts
    // (a Cognito PreAuthentication Lambda trigger) blocks a self-registered
    // user's login until they confirm their email, by throwing an Error whose
    // message starts with the stable, documented prefix "EmailNotVerified:"
    // (see that file's header for the exact contract). Cognito surfaces a
    // thrown PreAuthentication trigger error as UserLambdaValidationException
    // — NOT NotAuthorizedException — wrapping the trigger's own message inside
    // its own "PreAuthentication failed with error <message>." text. Matched
    // here by substring on the trigger's own stable prefix rather than
    // Cognito's wrapping text (which isn't this repo's contract to depend on
    // exactly), so this survives Cognito rewording its own wrapper. Any OTHER
    // UserLambdaValidationException (a different trigger entirely, or a
    // genuinely unexpected failure) falls through to this function's own
    // message / the generic fallback below — a narrow, specific carve-out,
    // not a blanket unwrap of every Lambda validation failure.
    case "UserLambdaValidationException":
      if (err.message?.includes("EmailNotVerified")) {
        return "Please verify your email before signing in — check your inbox " +
          "for the verification link we sent when you registered.";
      }
      return err.message || "Sign-in failed. Please try again.";
    default:
      return err.message || "Sign-in failed. Please try again.";
  }
}

// AUD-002 (blocker, Round 6 polish audit): the stable, verbatim raw-AWS
// substring this Cognito exception carries when a `email_verified=false`
// account (AccountRecovery.EMAIL_ONLY has no verified contact method to
// recover through) has no channel to send a reset code to. Matched as a
// substring, not the full message, per the audit's own fix direction
// ("Cognito's InvalidParameterException message text is stable enough to
// match"). Exported for auth.forgotPassword.test.ts to assert against the
// exact citation rather than duplicating it.
export const UNVERIFIED_ACCOUNT_MESSAGE_SIGNATURE = "no registered/verified email";

function friendlyForgotPasswordError(err: Error & { code?: string }): string {
  // NOTE: no case here should ever say anything account-specific
  // (e.g. "no such user") — see forgotPassword()'s doc comment. The
  // InvalidParameterException branch below is a NARROW exception to that:
  // it only ever fires for an account that already exists (a nonexistent
  // username hits UserNotFoundException instead, which forgotPassword()
  // collapses to `{kind:"sent"}` before this function is ever called — see
  // its own doc comment) and whose email happens to be unverified. That
  // distinguishing behavior (this call errors instead of advancing to the
  // confirm screen) already existed before this fix and is unchanged by
  // it — this only replaces what MESSAGE is shown once that's already true.
  switch (err.code) {
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts. Please wait a few minutes and try again.";
    case "InvalidParameterException":
      // AUD-002: this exception ALSO covers a different, non-input
      // condition — an unverified-email account has no channel
      // AccountRecovery.EMAIL_ONLY can send a reset code through, and
      // Cognito reports that as InvalidParameterException too, with this
      // specific raw message text (verbatim, live-confirmed by the audit):
      // "Cannot reset password for the user as there is no
      // registered/verified email or phone_number". That raw AWS string
      // must never reach the UI (see AUD-002) — it means nothing to a
      // builder and offers no next step. Every OTHER
      // InvalidParameterException here (e.g. a malformed email) keeps the
      // existing "Enter a valid email address." copy below.
      if (err.message?.includes(UNVERIFIED_ACCOUNT_MESSAGE_SIGNATURE)) {
        return "We can't send a reset code to this account — contact your DockPro administrator.";
      }
      return err.message || "Enter a valid email address.";
    default:
      return err.message || "Something went wrong. Please try again.";
  }
}

function friendlyConfirmForgotPasswordError(err: Error & { code?: string }): string {
  switch (err.code) {
    case "CodeMismatchException":
      return "That code is incorrect. Please check and try again.";
    case "ExpiredCodeException":
      return "That code has expired. Request a new one.";
    case "InvalidPasswordException":
      return friendlyPasswordPolicyMessage(err.message);
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts. Please wait a few minutes and try again.";
    default:
      return err.message || "Could not reset password. Please try again.";
  }
}
